import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { PaymentChannel } from '@/types'
import PaymentChannelList from '@/components/parameters/PaymentChannelList'
import PaymentChannelEditModal from '@/components/parameters/PaymentChannelEditModal'
import '../styles/TabStyles.css'

export default function PaymentChannelsTab() {
  const [channels, setChannels] = useState<PaymentChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState<PaymentChannel | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    loadChannels()
  }, [])

  const loadChannels = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.getPaymentChannels()
      if (response.success && response.data) {
        setChannels(response.data)
      } else {
        setError(response.message || '加载支付通道失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载支付通道失败')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async (id: string, enabled: boolean) => {
    setActionLoading(true)
    setError(null)
    try {
      const response = await apiClient.togglePaymentChannel(id, enabled)
      if (response.success) {
        setSuccessMessage('更新状态成功')
        setTimeout(() => setSuccessMessage(null), 3000)
        await loadChannels()
      } else {
        setError(response.message || '更新状态失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新状态失败')
    } finally {
      setActionLoading(false)
    }
  }

  const handleMoveUp = async (id: string) => {
    setActionLoading(true)
    setError(null)
    try {
      const response = await apiClient.updatePaymentChannelSort(id, 'up')
      if (response.success && response.data) {
        setChannels(response.data)
        setSuccessMessage('更新排序成功')
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        setError(response.message || '更新排序失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新排序失败')
    } finally {
      setActionLoading(false)
    }
  }

  const handleMoveDown = async (id: string) => {
    setActionLoading(true)
    setError(null)
    try {
      const response = await apiClient.updatePaymentChannelSort(id, 'down')
      if (response.success && response.data) {
        setChannels(response.data)
        setSuccessMessage('更新排序成功')
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        setError(response.message || '更新排序失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新排序失败')
    } finally {
      setActionLoading(false)
    }
  }

  const handleEdit = (channel: PaymentChannel) => {
    setEditingChannel(channel)
    setShowEditModal(true)
  }

  const handleSave = async (id: string, updates: Partial<PaymentChannel>) => {
    setActionLoading(true)
    setError(null)
    try {
      const response = await apiClient.updatePaymentChannel(id, updates)
      if (response.success) {
        setSuccessMessage('更新配置成功')
        setTimeout(() => setSuccessMessage(null), 3000)
        setShowEditModal(false)
        setEditingChannel(null)
        await loadChannels()
      } else {
        setError(response.message || '更新配置失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新配置失败')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <h3>支付通道配置</h3>
        <button
          className="btn-refresh"
          onClick={loadChannels}
          disabled={loading}
        >
          {loading ? '加载中...' : '刷新'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>✅ {successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>加载中...</p>
        </div>
      ) : (
        <PaymentChannelList
          channels={channels}
          onToggle={handleToggle}
          onEdit={handleEdit}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          loading={actionLoading}
        />
      )}

      <PaymentChannelEditModal
        isOpen={showEditModal}
        channel={editingChannel}
        onClose={() => {
          setShowEditModal(false)
          setEditingChannel(null)
        }}
        onSave={handleSave}
        loading={actionLoading}
      />
    </div>
  )
}
